import { Notice } from '@mnd-frontend/graphql-schema';
import { SystemInfo } from '@mnd-frontend/ui/publish/components';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router';
import { useQueryState } from '../../../../contexts/UseQueryStateContext';
import { usePressroomLiveQuery } from '../../../../generated/hooks';
import NewsroomSetup from '../../../../modules/newsroomSetup/NewsroomSetup';
import type { NewsroomSetupSteps } from '../../../../modules/newsroomSetup/types';
import ContactBookingModal from '../../../../modules/trial/ContactBookingModal';
import routes from '../../../../routes';

const NotLiveAlertElement = ({ pressroomId }: { pressroomId: string }) => {
  const [t] = useTranslation();
  const [showContactModal, setShowContactModal] = useState(false);
  const [, setOnboardingStep] = useQueryState<NewsroomSetupSteps>('onboarding-step', undefined);

  const { data, loading, error } = usePressroomLiveQuery({
    variables: {
      id: pressroomId,
    },
  });

  if (loading || error) return null;

  const pressroom = data?.pressroom;
  if (
    !pressroom ||
    !pressroom.source ||
    (pressroom.live === true && pressroom.source.trusted === true)
  ) {
    return null;
  }
  return (
    <>
      {pressroom.source.trusted ? (
        <SystemInfo
          action={{
            text: t('not_live_info_preview_go_live'),
            onClick: () => setOnboardingStep('branding'),
          }}
        >
          <Trans i18nKey="not_live_info_preview_text" />
        </SystemInfo>
      ) : (
        <SystemInfo
          action={{
            text: t('not_live_info_contact_sales'),
            onClick: () => setShowContactModal(true),
          }}
        >
          {pressroom.source.trial ? (
            <Trans i18nKey="not_live_info_trial_text" />
          ) : (
            <Trans i18nKey="not_live_info_plan_req_text" />
          )}
        </SystemInfo>
      )}
      {data?.me && (
        <NewsroomSetup user={data.me.user} pressroom={pressroom} showReadyToGoLive={true} />
      )}
      {data && (
        <ContactBookingModal
          show={showContactModal}
          defaultValues={{
            email: data.me?.user.email || '',
            phone: data.me?.user.phone || '',
          }}
          close={() => setShowContactModal(false)}
          onSuccessClose={() => setShowContactModal(false)}
          hubspotType="TRIAL_CUSTOMER_UPGRADE"
          noticeKey={Notice.TrialDemo}
          tracking={{
            event: 'Trial Modal - form submitted',
            traits: {
              cta_text: t('trial_book_demo'),
            },
          }}
        />
      )}
    </>
  );
};

const NotLiveAlert = ({ pressroomId }: { pressroomId: string }) => {
  const element = <NotLiveAlertElement pressroomId={pressroomId} />;

  return (
    <Routes>
      <Route path={routes.newsroomSettings.pattern} element={element} />
      <Route path={routes.publish.pattern} element={element} />
      <Route path={routes.publishContactPeopleEdit.pattern} element={element} />
      <Route path={routes.publishContactPeopleCreate.pattern} element={element} />
      <Route path={routes.publishContactPeopleList.pattern} element={element} />
      <Route path={routes.publishImagesEdit.pattern} element={element} />
      <Route path={routes.publishImagesList.pattern} element={element} />
      <Route path={routes.publishDistribute.pattern} element={element} />
      <Route path={routes.publishList.pattern} element={element} />
      <Route path={routes.publishEdit.pattern} element={element} />
      <Route path={routes.publishCreate.pattern} element={element} />
      <Route path="*" element={null} />
    </Routes>
  );
};

export default NotLiveAlert;
